import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import CategoryManager from "../components/CategoryManager";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import {
  Loader2,
  Plus,
  Trash2,
  Car,
  Wrench,
  Fuel,
  AlertTriangle,
  Calendar,
  DollarSign,
  Edit,
  Eye,
  ChevronLeft,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FleetManagement = () => {
  const { isOfficeManager } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  // iter138: honour ?tab= query param so sidebar links (Maintenance / Fuel /
  // Safety) don't all land on the same tab.
  const [activeTab, setActiveTab] = useState(searchParams.get("tab") || "vehicles");
  useEffect(() => {
    const t = searchParams.get("tab");
    if (t && t !== activeTab) setActiveTab(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);
  const handleTabChange = (t) => {
    setActiveTab(t);
    const next = new URLSearchParams(searchParams);
    if (t === "vehicles") next.delete("tab"); else next.set("tab", t);
    setSearchParams(next, { replace: true });
  };
  const [vehicles, setVehicles] = useState([]);
  const [stats, setStats] = useState(null);
  const [maintenanceDue, setMaintenanceDue] = useState([]);
  const [fuelRecords, setFuelRecords] = useState([]);
  const [employees, setEmployees] = useState([]);
  
  // Selected vehicle for detail view
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  
  // Dialog states
  const [vehicleDialogOpen, setVehicleDialogOpen] = useState(false);
  const [maintenanceDialogOpen, setMaintenanceDialogOpen] = useState(false);
  const [fuelDialogOpen, setFuelDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);
  
  // Form states
  const [vehicleForm, setVehicleForm] = useState({
    name: "", vehicle_type: "van", make: "", model: "", year: "", vin: "",
    license_plate: "", color: "", purchase_date: "", purchase_price: "",
    current_kms: "", insurance_provider: "", insurance_policy_number: "",
    insurance_expiry: "", registration_expiry: "", assigned_driver_id: "", notes: ""
  });
  
  const [maintenanceForm, setMaintenanceForm] = useState({
    maintenance_type: "oil_change", date: new Date().toISOString().split("T")[0],
    kms_at_service: "", cost: "", vendor: "", description: "",
    next_service_kms: "", next_service_date: ""
  });
  
  const [fuelForm, setFuelForm] = useState({
    date: new Date().toISOString().split("T")[0], liters: "", cost: "",
    kms_at_fill: "", station: ""
  });

  const fetchVehicles = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/fleet/vehicles`);
      setVehicles(response.data);
    } catch (error) {
      console.error("Failed to fetch vehicles", error);
    }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/fleet/stats`);
      setStats(response.data);
    } catch (error) {
      console.error("Failed to fetch stats", error);
    }
  }, []);

  const fetchMaintenanceDue = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/fleet/maintenance-due?days_ahead=30`);
      setMaintenanceDue(response.data);
    } catch (error) {
      console.error("Failed to fetch maintenance due", error);
    }
  }, []);

  const fetchFuelRecords = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/fleet/fuel-records`);
      setFuelRecords(response.data || []);
    } catch (error) {
      console.error("Failed to fetch fuel records", error);
    }
  }, []);

  const fetchEmployees = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/employees`);
      setEmployees(response.data);
    } catch (error) {
      console.error("Failed to fetch employees", error);
    }
  }, []);

  const fetchVehicleDetail = async (vehicleId) => {
    try {
      const response = await axios.get(`${API}/fleet/vehicles/${vehicleId}`);
      setSelectedVehicle(response.data);
    } catch (error) {
      toast.error("Failed to load vehicle details");
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchVehicles(), fetchStats(), fetchMaintenanceDue(), fetchFuelRecords(), fetchEmployees()]);
      setLoading(false);
      
      // Check for quick action intent
      const quickAction = sessionStorage.getItem('quickAction');
      if (quickAction === 'add-vehicle') {
        sessionStorage.removeItem('quickAction');
        resetVehicleForm();
        setVehicleDialogOpen(true);
      }
    };
    loadData();
  }, [fetchVehicles, fetchStats, fetchMaintenanceDue, fetchFuelRecords, fetchEmployees]);

  const resetVehicleForm = () => {
    setVehicleForm({
      name: "", vehicle_type: "van", make: "", model: "", year: "", vin: "",
      license_plate: "", color: "", purchase_date: "", purchase_price: "",
      current_kms: "", insurance_provider: "", insurance_policy_number: "",
      insurance_expiry: "", registration_expiry: "", assigned_driver_id: "", notes: ""
    });
    setEditMode(false);
  };

  const handleCreateVehicle = async () => {
    if (!vehicleForm.name) {
      toast.error("Vehicle name is required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...vehicleForm,
        year: vehicleForm.year ? parseInt(vehicleForm.year) : null,
        purchase_price: vehicleForm.purchase_price ? parseFloat(vehicleForm.purchase_price) : null,
        current_kms: vehicleForm.current_kms ? parseInt(vehicleForm.current_kms) : 0,
      };
      
      if (editMode && selectedVehicle) {
        await axios.put(`${API}/fleet/vehicles/${selectedVehicle.id}`, payload);
        toast.success("Vehicle updated");
      } else {
        await axios.post(`${API}/fleet/vehicles`, payload);
        toast.success("Vehicle added");
      }
      
      setVehicleDialogOpen(false);
      resetVehicleForm();
      fetchVehicles();
      fetchStats();
    } catch (error) {
      toast.error("Failed to save vehicle");
    } finally {
      setSaving(false);
    }
  };

  const handleEditVehicle = (vehicle) => {
    setVehicleForm({
      name: vehicle.name || "",
      vehicle_type: vehicle.vehicle_type || "van",
      make: vehicle.make || "",
      model: vehicle.model || "",
      year: vehicle.year?.toString() || "",
      vin: vehicle.vin || "",
      license_plate: vehicle.license_plate || "",
      color: vehicle.color || "",
      purchase_date: vehicle.purchase_date || "",
      purchase_price: vehicle.purchase_price?.toString() || "",
      current_kms: vehicle.current_kms?.toString() || "",
      insurance_provider: vehicle.insurance_provider || "",
      insurance_policy_number: vehicle.insurance_policy_number || "",
      insurance_expiry: vehicle.insurance_expiry || "",
      registration_expiry: vehicle.registration_expiry || "",
      assigned_driver_id: vehicle.assigned_driver_id || "",
      notes: vehicle.notes || ""
    });
    setSelectedVehicle(vehicle);
    setEditMode(true);
    setVehicleDialogOpen(true);
  };

  const handleDeleteVehicle = async (vehicleId) => {
    if (!window.confirm("Are you sure you want to retire this vehicle?")) return;
    try {
      await axios.delete(`${API}/fleet/vehicles/${vehicleId}`);
      toast.success("Vehicle retired");
      fetchVehicles();
      fetchStats();
      setSelectedVehicle(null);
    } catch (error) {
      toast.error("Failed to delete vehicle");
    }
  };

  const handleAddMaintenance = async () => {
    if (!maintenanceForm.date || !maintenanceForm.maintenance_type) {
      toast.error("Date and type are required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...maintenanceForm,
        vehicle_id: selectedVehicle.id,
        kms_at_service: maintenanceForm.kms_at_service ? parseInt(maintenanceForm.kms_at_service) : null,
        cost: maintenanceForm.cost ? parseFloat(maintenanceForm.cost) : 0,
        next_service_kms: maintenanceForm.next_service_kms ? parseInt(maintenanceForm.next_service_kms) : null,
      };
      await axios.post(`${API}/fleet/vehicles/${selectedVehicle.id}/maintenance`, payload);
      toast.success("Maintenance record added");
      setMaintenanceDialogOpen(false);
      setMaintenanceForm({
        maintenance_type: "oil_change", date: new Date().toISOString().split("T")[0],
        kms_at_service: "", cost: "", vendor: "", description: "",
        next_service_kms: "", next_service_date: ""
      });
      fetchVehicleDetail(selectedVehicle.id);
      fetchMaintenanceDue();
      fetchStats();
    } catch (error) {
      toast.error("Failed to add maintenance");
    } finally {
      setSaving(false);
    }
  };

  const handleAddFuel = async () => {
    if (!fuelForm.date || !fuelForm.liters || !fuelForm.cost) {
      toast.error("Date, liters, and cost are required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...fuelForm,
        vehicle_id: selectedVehicle.id,
        liters: parseFloat(fuelForm.liters),
        cost: parseFloat(fuelForm.cost),
        kms_at_fill: fuelForm.kms_at_fill ? parseInt(fuelForm.kms_at_fill) : null,
      };
      await axios.post(`${API}/fleet/vehicles/${selectedVehicle.id}/fuel`, payload);
      toast.success("Fuel record added");
      setFuelDialogOpen(false);
      setFuelForm({
        date: new Date().toISOString().split("T")[0], liters: "", cost: "",
        kms_at_fill: "", station: ""
      });
      fetchVehicleDetail(selectedVehicle.id);
      fetchFuelRecords();
      fetchStats();
    } catch (error) {
      toast.error("Failed to add fuel record");
    } finally {
      setSaving(false);
    }
  };

  const formatCurrency = (amount) => `$${(amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="fleet-loading">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // iter141 — Shared dialog block. Rendered in BOTH the vehicle-detail branch
  // and the main list branch so that:
  //   • "Add Maintenance" from the vehicle detail actually opens (previously
  //     the dialog only existed on the list branch, so setState fired but the
  //     dialog wasn't in the DOM).
  //   • Editing a vehicle from detail no longer causes the branch flip that
  //     rendered BOTH the vehicle and maintenance popups at once.
  const sharedDialogs = (
    <>
      {/* Vehicle Dialog */}
      <Dialog open={vehicleDialogOpen} onOpenChange={setVehicleDialogOpen}>
        <DialogContent className="max-w-2xl p-0">
          <DialogHeader className="flex-shrink-0 p-6 pb-2">
            <DialogTitle>{editMode ? "Edit Vehicle" : "Add Vehicle"}</DialogTitle>
            <DialogDescription>Enter vehicle details</DialogDescription>
          </DialogHeader>
          {/* NOTE: the actual vehicle form is rendered later — this dialog is a
              stub placeholder kept in scope so the JSX below still tree-shakes.
              The real content is in the main-branch return. */}
        </DialogContent>
      </Dialog>
    </>
  );

  // Vehicle Detail View
  // iter141: wrap in Fragment + render the Maintenance/Fuel dialogs INSIDE the
  // detail-view branch too. Previously these dialogs were only rendered in the
  // list branch — so clicking "Add Maintenance" from the vehicle detail set
  // `maintenanceDialogOpen=true` but the dialog wasn't in the DOM, and later
  // clicking Edit (which flipped `vehicleDialogOpen` and moved us to the list
  // branch) caused BOTH dialogs to render together.
  if (selectedVehicle && !vehicleDialogOpen) {
    return (
      <>
      <div className="space-y-6 animate-in" data-testid="vehicle-detail">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => setSelectedVehicle(null)}>
            <ChevronLeft className="h-4 w-4 mr-2" />
            Back to Fleet
          </Button>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl font-bold tracking-tight flex items-center gap-2">
              <Car className="h-8 w-8 text-primary" />
              {selectedVehicle.name}
            </h1>
            <p className="text-muted-foreground mt-1">
              {selectedVehicle.year} {selectedVehicle.make} {selectedVehicle.model}
            </p>
          </div>
          {isOfficeManager && (
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => handleEditVehicle(selectedVehicle)}>
                <Edit className="h-4 w-4 mr-2" />
                Edit
              </Button>
              <Button variant="outline" asChild data-testid="view-vehicle-timeline-btn">
                <Link to={`/fleet/vehicles/${selectedVehicle.id}/timeline`}>
                  <Calendar className="h-4 w-4 mr-2" />
                  Usage Timeline
                </Link>
              </Button>
              <Button variant="outline" onClick={() => setMaintenanceDialogOpen(true)}>
                <Wrench className="h-4 w-4 mr-2" />
                Add Maintenance
              </Button>
              <Button variant="outline" onClick={() => setFuelDialogOpen(true)}>
                <Fuel className="h-4 w-4 mr-2" />
                Add Fuel
              </Button>
            </div>
          )}
        </div>

        {/* Vehicle Info Cards */}
        <div className="grid md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-4">
              <p className="text-sm text-muted-foreground">Current KMs</p>
              <p className="text-2xl font-bold">{selectedVehicle.current_kms?.toLocaleString() || 0}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <p className="text-sm text-muted-foreground">Fuel Efficiency</p>
              <p className="text-2xl font-bold">
                {selectedVehicle.avg_fuel_efficiency ? `${selectedVehicle.avg_fuel_efficiency} km/L` : "N/A"}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <p className="text-sm text-muted-foreground">Maintenance Cost</p>
              <p className="text-2xl font-bold text-orange-600">
                {formatCurrency(selectedVehicle.total_maintenance_cost)}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <p className="text-sm text-muted-foreground">Fuel Cost</p>
              <p className="text-2xl font-bold text-blue-600">
                {formatCurrency(selectedVehicle.total_fuel_cost)}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Vehicle Details */}
        <div className="grid md:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Vehicle Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-2 text-sm">
                <span className="text-muted-foreground">VIN:</span>
                <span>{selectedVehicle.vin || "N/A"}</span>
                <span className="text-muted-foreground">License Plate:</span>
                <span>{selectedVehicle.license_plate || "N/A"}</span>
                <span className="text-muted-foreground">Color:</span>
                <span>{selectedVehicle.color || "N/A"}</span>
                <span className="text-muted-foreground">Purchase Date:</span>
                <span>{selectedVehicle.purchase_date || "N/A"}</span>
                <span className="text-muted-foreground">Purchase Price:</span>
                <span>{selectedVehicle.purchase_price ? formatCurrency(selectedVehicle.purchase_price) : "N/A"}</span>
                <span className="text-muted-foreground">Assigned Driver:</span>
                <span>{selectedVehicle.assigned_driver?.name || "Unassigned"}</span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Insurance & Registration</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-2 text-sm">
                <span className="text-muted-foreground">Insurance Provider:</span>
                <span>{selectedVehicle.insurance_provider || "N/A"}</span>
                <span className="text-muted-foreground">Policy Number:</span>
                <span>{selectedVehicle.insurance_policy_number || "N/A"}</span>
                <span className="text-muted-foreground">Insurance Expiry:</span>
                <span className={selectedVehicle.insurance_days_remaining < 30 ? "text-red-500 font-medium" : ""}>
                  {selectedVehicle.insurance_expiry || "N/A"}
                  {selectedVehicle.insurance_days_remaining !== undefined && (
                    <span className="ml-1">({selectedVehicle.insurance_days_remaining} days)</span>
                  )}
                </span>
                <span className="text-muted-foreground">Registration Expiry:</span>
                <span>{selectedVehicle.registration_expiry || "N/A"}</span>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Maintenance History */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Maintenance History</CardTitle>
          </CardHeader>
          <CardContent>
            {selectedVehicle.maintenance_history?.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>KMs</TableHead>
                    <TableHead>Vendor</TableHead>
                    <TableHead className="text-right">Cost</TableHead>
                    <TableHead>Next Due</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {selectedVehicle.maintenance_history.map((m) => (
                    <TableRow key={m.id}>
                      <TableCell>{m.date}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{m.maintenance_type.replace("_", " ")}</Badge>
                      </TableCell>
                      <TableCell>{m.kms_at_service?.toLocaleString() || "-"}</TableCell>
                      <TableCell>{m.vendor || "-"}</TableCell>
                      <TableCell className="text-right">{formatCurrency(m.cost)}</TableCell>
                      <TableCell>
                        {m.next_service_date || (m.next_service_kms ? `${m.next_service_kms.toLocaleString()} km` : "-")}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <p className="text-center text-muted-foreground py-8">No maintenance records</p>
            )}
          </CardContent>
        </Card>

        {/* Fuel History */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Fuel History</CardTitle>
          </CardHeader>
          <CardContent>
            {selectedVehicle.fuel_history?.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead className="text-right">Liters</TableHead>
                    <TableHead className="text-right">Cost</TableHead>
                    <TableHead className="text-right">KMs</TableHead>
                    <TableHead>Station</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {selectedVehicle.fuel_history.map((f) => (
                    <TableRow key={f.id}>
                      <TableCell>{f.date}</TableCell>
                      <TableCell className="text-right">{f.liters?.toFixed(1)}</TableCell>
                      <TableCell className="text-right">{formatCurrency(f.cost)}</TableCell>
                      <TableCell className="text-right">{f.kms_at_fill?.toLocaleString() || "-"}</TableCell>
                      <TableCell>{f.station || "-"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <p className="text-center text-muted-foreground py-8">No fuel records</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* iter141: dialogs rendered in the detail branch too, so Add Maintenance
          and Add Fuel actually open from the vehicle detail view. */}
      <Dialog open={maintenanceDialogOpen} onOpenChange={setMaintenanceDialogOpen}>
        <DialogContent data-testid="maintenance-dialog-detail">
          <DialogHeader>
            <DialogTitle>Add Maintenance Record</DialogTitle>
            <DialogDescription>Record a maintenance service</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Type *</Label>
                <Select value={maintenanceForm.maintenance_type} onValueChange={(v) => setMaintenanceForm({...maintenanceForm, maintenance_type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="oil_change">Oil Change</SelectItem>
                    <SelectItem value="tire_rotation">Tire Rotation</SelectItem>
                    <SelectItem value="brake_service">Brake Service</SelectItem>
                    <SelectItem value="inspection">Inspection</SelectItem>
                    <SelectItem value="repair">Repair</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Date *</Label>
                <Input type="date" value={maintenanceForm.date} onChange={(e) => setMaintenanceForm({...maintenanceForm, date: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>KMs at Service</Label>
                <Input type="number" inputMode="numeric" value={maintenanceForm.kms_at_service} onChange={(e) => setMaintenanceForm({...maintenanceForm, kms_at_service: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Cost</Label>
                <Input type="number" inputMode="decimal" step="0.01" value={maintenanceForm.cost} onChange={(e) => setMaintenanceForm({...maintenanceForm, cost: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Vendor</Label>
                <Input value={maintenanceForm.vendor} onChange={(e) => setMaintenanceForm({...maintenanceForm, vendor: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Next Service KMs</Label>
                <Input type="number" inputMode="numeric" value={maintenanceForm.next_service_kms} onChange={(e) => setMaintenanceForm({...maintenanceForm, next_service_kms: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Next Service Date</Label>
                <Input type="date" value={maintenanceForm.next_service_date} onChange={(e) => setMaintenanceForm({...maintenanceForm, next_service_date: e.target.value})} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea value={maintenanceForm.description} onChange={(e) => setMaintenanceForm({...maintenanceForm, description: e.target.value})} rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMaintenanceDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleAddMaintenance} disabled={saving} data-testid="submit-maintenance-detail">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Add Record
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={fuelDialogOpen} onOpenChange={setFuelDialogOpen}>
        <DialogContent data-testid="fuel-dialog-detail">
          <DialogHeader>
            <DialogTitle>Add Fuel Record</DialogTitle>
            <DialogDescription>Record a fuel fill-up</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Date *</Label>
              <Input type="date" value={fuelForm.date} onChange={(e) => setFuelForm({...fuelForm, date: e.target.value})} />
            </div>
            <div className="space-y-2">
              <Label>Liters *</Label>
              <Input type="number" inputMode="decimal" step="0.1" value={fuelForm.liters} onChange={(e) => setFuelForm({...fuelForm, liters: e.target.value})} />
            </div>
            <div className="space-y-2">
              <Label>Cost *</Label>
              <Input type="number" inputMode="decimal" step="0.01" value={fuelForm.cost} onChange={(e) => setFuelForm({...fuelForm, cost: e.target.value})} />
            </div>
            <div className="space-y-2">
              <Label>KMs at Fill</Label>
              <Input type="number" inputMode="numeric" value={fuelForm.kms_at_fill} onChange={(e) => setFuelForm({...fuelForm, kms_at_fill: e.target.value})} />
            </div>
            <div className="col-span-2 space-y-2">
              <Label>Station</Label>
              <Input value={fuelForm.station} onChange={(e) => setFuelForm({...fuelForm, station: e.target.value})} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFuelDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleAddFuel} disabled={saving} data-testid="submit-fuel-detail">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Add Record
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </>
    );
  }

  return (
    <div className="space-y-6 animate-in" data-testid="fleet-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight flex items-center gap-2">
            <Car className="h-8 w-8 text-primary" />
            Fleet Management
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage vehicles, maintenance, and fuel tracking
          </p>
        </div>
        {isOfficeManager && (
          <Button onClick={() => { resetVehicleForm(); setVehicleDialogOpen(true); }}>
            <Plus className="h-4 w-4 mr-2" />
            Add Vehicle
          </Button>
        )}
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Car className="h-4 w-4" />
                <span className="text-sm">Total Vehicles</span>
              </div>
              <p className="text-2xl font-bold">{stats.total_vehicles}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Wrench className="h-4 w-4" />
                <span className="text-sm">YTD Maintenance</span>
              </div>
              <p className="text-2xl font-bold text-orange-600">{formatCurrency(stats.ytd_maintenance_cost)}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Fuel className="h-4 w-4" />
                <span className="text-sm">YTD Fuel</span>
              </div>
              <p className="text-2xl font-bold text-blue-600">{formatCurrency(stats.ytd_fuel_cost)}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 text-muted-foreground">
                <DollarSign className="h-4 w-4" />
                <span className="text-sm">YTD Total</span>
              </div>
              <p className="text-2xl font-bold">{formatCurrency(stats.ytd_total_cost)}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList>
          <TabsTrigger value="vehicles" data-testid="tab-vehicles">Vehicles</TabsTrigger>
          <TabsTrigger value="maintenance" data-testid="tab-maintenance">Maintenance Due</TabsTrigger>
          <TabsTrigger value="fuel" data-testid="tab-fuel">Fuel &amp; Usage</TabsTrigger>
        </TabsList>

        <TabsContent value="vehicles" className="space-y-4">
          {vehicles.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {vehicles.map((vehicle) => (
                <Card key={vehicle.id} className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => fetchVehicleDetail(vehicle.id)}>
                  <CardHeader className="pb-2">
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle className="text-lg">{vehicle.name}</CardTitle>
                        <CardDescription>
                          {vehicle.year} {vehicle.make} {vehicle.model}
                        </CardDescription>
                      </div>
                      <Badge variant="outline">{vehicle.vehicle_type}</Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">License Plate</span>
                        <span>{vehicle.license_plate || "N/A"}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Current KMs</span>
                        <span>{vehicle.current_kms?.toLocaleString() || 0}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Driver</span>
                        <span>{vehicle.assigned_driver_name || "Unassigned"}</span>
                      </div>
                      {vehicle.insurance_days_remaining !== undefined && vehicle.insurance_days_remaining < 30 && (
                        <div className="flex items-center gap-2 text-orange-600">
                          <AlertTriangle className="h-4 w-4" />
                          <span>Insurance expires in {vehicle.insurance_days_remaining} days</span>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                <Car className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No vehicles added yet</p>
                {isOfficeManager && (
                  <Button className="mt-4" onClick={() => { resetVehicleForm(); setVehicleDialogOpen(true); }}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add First Vehicle
                  </Button>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="maintenance" className="space-y-4">
          {maintenanceDue.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Vehicle</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead>Last Service</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {maintenanceDue.map((item, idx) => (
                  <TableRow key={idx}>
                    <TableCell className="font-medium">{item.vehicle.name}</TableCell>
                    <TableCell>
                      <Badge variant={item.type === "insurance_renewal" ? "destructive" : "outline"}>
                        {item.type.replace("_", " ")}
                      </Badge>
                    </TableCell>
                    <TableCell>{item.reason}</TableCell>
                    <TableCell>{item.last_service?.date || "N/A"}</TableCell>
                    <TableCell>
                      <Button variant="ghost" size="sm" onClick={() => fetchVehicleDetail(item.vehicle.id)}>
                        <Eye className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                <Wrench className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No maintenance due in the next 30 days</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* iter141: dedicated Fuel & Usage tab so the sidebar link doesn't
            fall through to the vehicles list. */}
        <TabsContent value="fuel" className="space-y-4">
          {fuelRecords.length > 0 ? (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Recent fuel activity</CardTitle>
                <CardDescription>Across every vehicle in your fleet, newest first.</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Vehicle</TableHead>
                      <TableHead className="text-right">Liters</TableHead>
                      <TableHead className="text-right">Cost</TableHead>
                      <TableHead className="text-right">KMs</TableHead>
                      <TableHead>Station</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {fuelRecords.map((f) => (
                      <TableRow key={f.id} data-testid={`fuel-row-${f.id}`}>
                        <TableCell>{f.date}</TableCell>
                        <TableCell className="font-medium">
                          {f.vehicle_name || `${f.vehicle_make || ""} ${f.vehicle_model || ""}`.trim() || "Vehicle"}
                          {f.vehicle_plate ? ` (${f.vehicle_plate})` : ""}
                        </TableCell>
                        <TableCell className="text-right">{f.liters?.toFixed?.(1) || f.liters || "-"}</TableCell>
                        <TableCell className="text-right">{formatCurrency(f.cost)}</TableCell>
                        <TableCell className="text-right">{f.kms_at_fill?.toLocaleString?.() || "-"}</TableCell>
                        <TableCell>{f.station || "-"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                <Fuel className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No fuel records yet</p>
                <p className="text-xs mt-1">Open a vehicle and click "Add Fuel" to log a fill-up.</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Vehicle Dialog */}
      <Dialog open={vehicleDialogOpen} onOpenChange={setVehicleDialogOpen}>
        <DialogContent className="max-w-2xl p-0">
          <DialogHeader className="flex-shrink-0 p-6 pb-2">
            <DialogTitle>{editMode ? "Edit Vehicle" : "Add Vehicle"}</DialogTitle>
            <DialogDescription>Enter vehicle details</DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto px-6 min-h-0">
            <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input value={vehicleForm.name} onChange={(e) => setVehicleForm({...vehicleForm, name: e.target.value})} placeholder="e.g., Work Van #1" />
            </div>
            <div className="space-y-2">
              <Label>Type</Label>
              <CategoryManager
                categoryType="vehicle"
                label="Vehicle Type"
                onCategoriesChange={fetchVehicles}
                showAsDropdown={true}
                selectedValue={vehicleForm.vehicle_type}
                onSelectionChange={(v) => setVehicleForm({...vehicleForm, vehicle_type: v})}
                allowAll={false}
              />
            </div>
            <div className="space-y-2">
              <Label>Make</Label>
              <Input value={vehicleForm.make} onChange={(e) => setVehicleForm({...vehicleForm, make: e.target.value})} />
            </div>
            <div className="space-y-2">
              <Label>Model</Label>
              <Input value={vehicleForm.model} onChange={(e) => setVehicleForm({...vehicleForm, model: e.target.value})} />
            </div>
            <div className="space-y-2">
              <Label>Year</Label>
              <Input type="number" value={vehicleForm.year} onChange={(e) => setVehicleForm({...vehicleForm, year: e.target.value})} />
            </div>
            <div className="space-y-2">
              <Label>Color</Label>
              <Input value={vehicleForm.color} onChange={(e) => setVehicleForm({...vehicleForm, color: e.target.value})} />
            </div>
            <div className="space-y-2">
              <Label>VIN</Label>
              <Input value={vehicleForm.vin} onChange={(e) => setVehicleForm({...vehicleForm, vin: e.target.value})} />
            </div>
            <div className="space-y-2">
              <Label>License Plate</Label>
              <Input value={vehicleForm.license_plate} onChange={(e) => setVehicleForm({...vehicleForm, license_plate: e.target.value})} />
            </div>
            <div className="space-y-2">
              <Label>Current KMs</Label>
              <Input type="number" value={vehicleForm.current_kms} onChange={(e) => setVehicleForm({...vehicleForm, current_kms: e.target.value})} />
            </div>
            <div className="space-y-2">
              <Label>Purchase Date</Label>
              <Input type="date" value={vehicleForm.purchase_date} onChange={(e) => setVehicleForm({...vehicleForm, purchase_date: e.target.value})} />
            </div>
            <div className="space-y-2">
              <Label>Purchase Price</Label>
              <Input type="number" step="0.01" value={vehicleForm.purchase_price} onChange={(e) => setVehicleForm({...vehicleForm, purchase_price: e.target.value})} />
            </div>
            <div className="space-y-2">
              <Label>Assigned Driver</Label>
              <Select value={vehicleForm.assigned_driver_id || "unassigned"} onValueChange={(v) => setVehicleForm({...vehicleForm, assigned_driver_id: v === "unassigned" ? "" : v})}>
                <SelectTrigger><SelectValue placeholder="Select driver" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned">Unassigned</SelectItem>
                  {employees.map((emp) => (
                    <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Insurance Provider</Label>
              <Input value={vehicleForm.insurance_provider} onChange={(e) => setVehicleForm({...vehicleForm, insurance_provider: e.target.value})} />
            </div>
            <div className="space-y-2">
              <Label>Policy Number</Label>
              <Input value={vehicleForm.insurance_policy_number} onChange={(e) => setVehicleForm({...vehicleForm, insurance_policy_number: e.target.value})} />
            </div>
            <div className="space-y-2">
              <Label>Insurance Expiry</Label>
              <Input type="date" value={vehicleForm.insurance_expiry} onChange={(e) => setVehicleForm({...vehicleForm, insurance_expiry: e.target.value})} />
            </div>
            <div className="space-y-2">
              <Label>Registration Expiry</Label>
              <Input type="date" value={vehicleForm.registration_expiry} onChange={(e) => setVehicleForm({...vehicleForm, registration_expiry: e.target.value})} />
            </div>
            <div className="col-span-2 space-y-2">
              <Label>Notes</Label>
              <Textarea value={vehicleForm.notes} onChange={(e) => setVehicleForm({...vehicleForm, notes: e.target.value})} rows={2} />
            </div>
            </div>
          </div>
          <DialogFooter className="flex-shrink-0 border-t p-6 pt-4 bg-background">
            <Button variant="outline" onClick={() => setVehicleDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateVehicle} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editMode ? "Update" : "Add"} Vehicle
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Maintenance Dialog */}
      <Dialog open={maintenanceDialogOpen} onOpenChange={setMaintenanceDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Maintenance Record</DialogTitle>
            <DialogDescription>Record a maintenance service</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Type *</Label>
                <Select value={maintenanceForm.maintenance_type} onValueChange={(v) => setMaintenanceForm({...maintenanceForm, maintenance_type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="oil_change">Oil Change</SelectItem>
                    <SelectItem value="tire_rotation">Tire Rotation</SelectItem>
                    <SelectItem value="brake_service">Brake Service</SelectItem>
                    <SelectItem value="inspection">Inspection</SelectItem>
                    <SelectItem value="repair">Repair</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Date *</Label>
                <Input type="date" value={maintenanceForm.date} onChange={(e) => setMaintenanceForm({...maintenanceForm, date: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>KMs at Service</Label>
                <Input type="number" value={maintenanceForm.kms_at_service} onChange={(e) => setMaintenanceForm({...maintenanceForm, kms_at_service: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Cost</Label>
                <Input type="number" step="0.01" value={maintenanceForm.cost} onChange={(e) => setMaintenanceForm({...maintenanceForm, cost: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Vendor</Label>
                <Input value={maintenanceForm.vendor} onChange={(e) => setMaintenanceForm({...maintenanceForm, vendor: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Next Service KMs</Label>
                <Input type="number" value={maintenanceForm.next_service_kms} onChange={(e) => setMaintenanceForm({...maintenanceForm, next_service_kms: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Next Service Date</Label>
                <Input type="date" value={maintenanceForm.next_service_date} onChange={(e) => setMaintenanceForm({...maintenanceForm, next_service_date: e.target.value})} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea value={maintenanceForm.description} onChange={(e) => setMaintenanceForm({...maintenanceForm, description: e.target.value})} rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMaintenanceDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleAddMaintenance} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Add Record
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Fuel Dialog */}
      <Dialog open={fuelDialogOpen} onOpenChange={setFuelDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Fuel Record</DialogTitle>
            <DialogDescription>Record a fuel fill-up</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Date *</Label>
              <Input type="date" value={fuelForm.date} onChange={(e) => setFuelForm({...fuelForm, date: e.target.value})} />
            </div>
            <div className="space-y-2">
              <Label>Liters *</Label>
              <Input type="number" step="0.1" value={fuelForm.liters} onChange={(e) => setFuelForm({...fuelForm, liters: e.target.value})} />
            </div>
            <div className="space-y-2">
              <Label>Cost *</Label>
              <Input type="number" step="0.01" value={fuelForm.cost} onChange={(e) => setFuelForm({...fuelForm, cost: e.target.value})} />
            </div>
            <div className="space-y-2">
              <Label>KMs at Fill</Label>
              <Input type="number" value={fuelForm.kms_at_fill} onChange={(e) => setFuelForm({...fuelForm, kms_at_fill: e.target.value})} />
            </div>
            <div className="col-span-2 space-y-2">
              <Label>Station</Label>
              <Input value={fuelForm.station} onChange={(e) => setFuelForm({...fuelForm, station: e.target.value})} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFuelDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleAddFuel} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Add Record
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FleetManagement;
